// MUI imports removed - using Tailwind CSS instead
import Input from "../components/input/Input";

import { useState, useEffect, useCallback } from "react";
import { login } from "../features/authorization/authService";
import LoginButton from "../components/login-button/LoginButton";

import { Trans } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  loginUserAsync,
  registerUserAsync,
  resetPasswordAsync,
} from "../features/authentication/authenticationApi";
import { selectAuthenticationStatus } from "../features/authentication/authenticationSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { HttpStatusCode } from "axios";
import { StringExtension } from "../features/extensions/StringExtension";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../routes";
import { UserLoginRequest } from "../features/authentication/UserLoginRequest";
import { useTheme } from "../features/theme/useTheme";

const LoginPage = () => {
  const { t } = useTranslation();
  const { themeMode } = useTheme();
  const isDark = themeMode === 'dark' ||
    (themeMode === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authStatus = useAppSelector(selectAuthenticationStatus);
  const stringExtension = new StringExtension();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [repeatPassword, setRepeatPassword] = useState<string>("");
  const [repeatPasswordError, setRepeatPasswordError] = useState<string | null>(
    null
  );
  // Password visibility is now handled within the custom TextField component
  const [isHeightLessThan864, setIsHeightLessThan864] =
    useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isResetPassword, setIsResetPassword] = useState<boolean>(false);
  const [resetEmail, setResetEmail] = useState<string>("");
  const [resetEmailError, setResetEmailError] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  useEffect(() => {
    if ((location.state as { register?: boolean })?.register) {
      setIsRegistering(true);
    }
  }, [location.state]);

  useEffect(() => {
    setLoading(authStatus === "loading");
  }, [authStatus]);

  useEffect(() => {
    const handleResize = () => {
      setIsHeightLessThan864(window.innerHeight < 864);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflowY = "auto";

    return () => {
      document.body.style.overflowY = "";
    };
  }, []);

  const validatePassword = (password: string): boolean => {
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNonAlphanumeric = /[^a-zA-Z0-9]/.test(password);
    return hasLowerCase && hasUpperCase && hasNonAlphanumeric;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLoginAsync = useCallback(
    async (provider: "Google" | "Microsoft" | "Facebook") => {
      setLoading(true);
      setError(null);

      try {
        await login(provider);
        setTimeout(() => setLoading(false), 1000);
      } catch (error) {
        setLoading(false);
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(String(error));
        }
      }
    },
    []
  );

  const handleEmailLogin = useCallback(async () => {
    setError(null);
    setEmailError(null);
    setPasswordError(null);

    if (!validateEmail(email)) {
      setEmailError(t("auth.errors.invalidEmailFormat"));
      return;
    }

    if (stringExtension.isEmpty(email)) {
      setEmailError(t("auth.errors.emailRequired"));
      return;
    }

    if (stringExtension.isEmpty(password)) {
      setPasswordError(t("auth.errors.passwordRequired"));
      return;
    }

    setLoading(true);

    try {
      const userLoginRequest: UserLoginRequest = {
        email: email,
        password: password,
      };

      const resultAction = await dispatch(loginUserAsync(userLoginRequest));

      if (loginUserAsync.fulfilled.match(resultAction)) {
        navigate("/");
      } else if (loginUserAsync.rejected.match(resultAction)) {
        const errorResponse = resultAction.payload;
        if (errorResponse) {
          switch (errorResponse.status) {
            case HttpStatusCode.BadRequest:
              setError(t("auth.errors.invalidRequest"));
              break;
            case HttpStatusCode.Unauthorized:
              setError(t("auth.errors.unauthorized"));
              break;
            case HttpStatusCode.InternalServerError:
              setError(t("auth.errors.serverError"));
              break;
            case HttpStatusCode.Forbidden:
              setError(t("auth.errors.accountNotVerified"));
              break;
            default:
              setError(t("auth.errors.unknownError"));
              break;
          }
        } else {
          setError(t("auth.errors.unknownError"));
        }
      }
    } catch (error) {
      setError(t("auth.errors.unexpectedError"));
    } finally {
      setLoading(false);
    }
  }, [email, password, dispatch, navigate, t, stringExtension]);

  const handleRegister = async () => {
    setError(null);
    setEmailError(null);
    setPasswordError(null);
    setRepeatPasswordError(null);
    setTermsError(null);

    let hasError = false;

    if (stringExtension.isEmpty(firstName)) {
      hasError = true;
    }

    if (stringExtension.isEmpty(lastName)) {
      hasError = true;
    }

    if (stringExtension.isEmpty(email)) {
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailError(t("auth.errors.invalidEmailFormat"));
      hasError = true;
    }

    if (stringExtension.isEmpty(password)) {
      hasError = true;
    } else if (!validatePassword(password)) {
      setPasswordError(t("auth.errors.passwordTooWeak"));
      hasError = true;
    }

    if (stringExtension.isEmpty(repeatPassword)) {
      hasError = true;
    } else if (repeatPassword !== password) {
      setRepeatPasswordError(t("auth.errors.passwordsNotMatch"));
      hasError = true;
    }

    if (!agreedToTerms) {
      setTermsError(t("auth.errors.termsRequired"));
      hasError = true;
    }

    if (hasError) {
      return;
    }

    try {
      const resultAction = await dispatch(
        registerUserAsync({
          firstName,
          lastName,
          email,
          password,
        })
      );

      if (registerUserAsync.rejected.match(resultAction)) {
        const errorResponse = resultAction.payload;
        if (errorResponse) {
          switch (errorResponse.status) {
            case HttpStatusCode.BadRequest:
              setError(t("auth.errors.invalidRequest"));
              break;
            case HttpStatusCode.Unauthorized:
              setError(t("auth.errors.unauthorized"));
              break;
            case HttpStatusCode.InternalServerError:
              setError(t("auth.errors.serverError"));
              break;
            case HttpStatusCode.Conflict:
              setError(t("auth.errors.emailAlreadyInUse"));
              break;
            case HttpStatusCode.Forbidden:
              setError(t("auth.errors.accountNotVerified"));
              break;
            default:
              setError(t("auth.errors.unknownError"));
              break;
          }
        } else {
          setError(t("auth.errors.unknownError"));
        }
      } else {
        setIsRegistering(false);
        setError(null);
        navigate(ROUTES.EMAIL_CONFIRMATION, { state: { email } });
      }
    } catch (error) {
      setError(t("auth.errors.registrationFailed"));
    }
  };

  const handleResetPassword = async () => {
    setError(null);
    setResetEmailError(null);

    if (!validateEmail(resetEmail)) {
      setResetEmailError(t("auth.errors.invalidEmailFormat"));
      return;
    }

    if (stringExtension.isEmpty(resetEmail)) {
      setResetEmailError(t("auth.errors.emailRequired"));
      return;
    }

    setLoading(true);
    try {
      const resultAction = await dispatch(resetPasswordAsync(resetEmail));

      if (resetPasswordAsync.fulfilled.match(resultAction)) {
        setError(null);
        setIsResetPassword(false);
        clearResetFields();
        navigate(ROUTES.RESET_PASSWORD_EMAIL_SENT, {
          state: { email: resetEmail },
        });
      } else if (resetPasswordAsync.rejected.match(resultAction)) {
        const errorResponse = resultAction.payload;
        if (errorResponse) {
          switch (errorResponse.status) {
            case HttpStatusCode.BadRequest:
              setError(t("auth.errors.invalidEmailFormat"));
              break;
            case HttpStatusCode.NotFound:
              setError(t("auth.errors.emailNotRegistered"));
              break;
            case HttpStatusCode.InternalServerError:
              setError(t("auth.errors.serverError"));
              break;
            default:
              setError(errorResponse.message || t("auth.errors.unknownError"));
              break;
          }
        } else {
          setError(t("auth.errors.unknownError"));
        }
      }
    } catch (error) {
      setError(t("auth.errors.unexpectedError"));
    } finally {
      setLoading(false);
    }
  };

  const clearInputFields = useCallback(() => {
    setEmail("");
    setPassword("");
    setRepeatPassword("");
    setFirstName("");
    setLastName("");
    setRepeatPasswordError(null);
    setError(null);
    setEmailError(null);
    setPasswordError(null);
    setAgreedToTerms(false);
    setTermsError(null);
  }, []);

  const clearResetFields = useCallback(() => {
    setResetEmail("");
    setResetEmailError(null);
    setError(null);
  }, []);

  // Handle Enter key press for form submission
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !loading) {
        if (isResetPassword) {
          handleResetPassword();
        } else if (isRegistering) {
          handleRegister();
        } else {
          handleEmailLogin();
        }
      }
    },
    [loading, isResetPassword, isRegistering, handleEmailLogin]
  );

  useEffect(() => {
    const headerHeight = document.querySelector("header")?.clientHeight || 0;
    document.documentElement.style.setProperty(
      "--header-height",
      `${headerHeight}px`
    );
  }, []);

  const isRegisterFormValid = () => {
    return (
      !stringExtension.isEmpty(firstName) &&
      !stringExtension.isEmpty(lastName) &&
      !stringExtension.isEmpty(email) &&
      !stringExtension.isEmpty(password) &&
      !stringExtension.isEmpty(repeatPassword) &&
      !emailError &&
      !passwordError &&
      !repeatPasswordError &&
      agreedToTerms
    );
  };

  return (
    <div
      className={`
        max-w-[552px] mx-auto flex justify-center
        ${
          isHeightLessThan864
            ? "h-auto px-4 py-8"
            : "h-[calc(100vh-var(--header-height,80px))] py-0"
        }
      `}
      onKeyPress={handleKeyPress}
      tabIndex={0}
    >
      <div
        className={`
          max-w-[800px] p-2 sm:p-5 md:p-5 lg:p-5 xl:p-5 rounded-3xl
          shadow-[0px_5px_15px_rgba(0,0,0,0.1)]
          flex flex-col items-center text-center
          h-fit w-full relative
          border border-solid border-primary-40 dark:border-primary-90
          ${isHeightLessThan864 ? "top-0" : "top-[10%]"}
        `}
      >
        <div>
          <svg
            width={250}
            height={42}
            viewBox="0 0 139 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="fill-grey-20 dark:fill-white"
              d="M4.848 17.096C3.984 17.096 3.28 16.864 2.736 16.4C2.192 15.936 1.92 15.232 1.92 14.288V7.088H0.216V5.984C1 5.68 1.696 5.28 2.304 4.784C2.928 4.272 3.424 3.672 3.792 2.984H5.352V5.528H8.424V7.088H5.328V14.096C5.328 14.544 5.432 14.872 5.64 15.08C5.848 15.272 6.128 15.368 6.48 15.368C6.752 15.368 7.04 15.312 7.344 15.2C7.664 15.088 7.952 14.944 8.208 14.768L8.688 15.608C8.192 16.056 7.592 16.416 6.888 16.688C6.2 16.96 5.52 17.096 4.848 17.096ZM9.65363 16.016C9.95763 15.952 10.1896 15.88 10.3496 15.8C10.5256 15.72 10.7096 15.592 10.9016 15.416V8.504C10.9016 7.928 10.7896 7.536 10.5656 7.328C10.3576 7.104 9.97363 6.976 9.41363 6.944L9.48563 6.008C9.72563 5.96 10.1576 5.888 10.7816 5.792C12.4936 5.504 13.5256 5.304 13.8776 5.192L14.2856 5.408V15.416C14.6856 15.72 15.1976 15.936 15.8216 16.064L15.7256 17H9.55762L9.65363 16.016ZM12.3656 3.752C11.7256 3.752 11.2456 3.608 10.9256 3.32C10.6056 3.032 10.4456 2.616 10.4456 2.072C10.4456 0.951999 11.0856 0.391999 12.3656 0.391999C12.9896 0.391999 13.4616 0.535999 13.7816 0.823999C14.1016 1.112 14.2616 1.528 14.2616 2.072C14.2616 2.616 14.1016 3.032 13.7816 3.32C13.4616 3.608 12.9896 3.752 12.3656 3.752ZM17.0364 16.016C17.3404 15.952 17.5724 15.88 17.7324 15.8C17.9084 15.72 18.1004 15.592 18.3084 15.416V8.504C18.3084 7.928 18.1964 7.536 17.9724 7.328C17.7484 7.104 17.3564 6.976 16.7964 6.944L16.8684 6.008L17.6604 5.864C19.3564 5.576 20.4604 5.352 20.9724 5.192L21.4044 5.408L21.5964 6.8C22.0124 6.288 22.5084 5.896 23.0844 5.624C23.6764 5.336 24.3244 5.192 25.0284 5.192C25.7964 5.192 26.4604 5.368 27.0204 5.72C27.5964 6.056 28.0204 6.536 28.2924 7.16C28.6924 6.552 29.2124 6.072 29.8524 5.72C30.4924 5.368 31.2044 5.192 31.9884 5.192C33.0764 5.192 33.9484 5.52 34.6044 6.176C35.2604 6.832 35.5884 7.736 35.5884 8.888V15.416C35.9884 15.72 36.5004 15.936 37.1244 16.064L37.0284 17H30.9564L31.0284 16.064C31.4764 15.968 31.8684 15.752 32.2044 15.416V9.104C32.2044 7.568 31.7164 6.8 30.7404 6.8C30.3084 6.8 29.8924 6.936 29.4924 7.208C29.1084 7.464 28.8044 7.808 28.5804 8.24C28.6124 8.528 28.6284 8.744 28.6284 8.888V15.416C28.9964 15.704 29.4764 15.92 30.0684 16.064L29.9724 17H23.9964L24.0684 16.064C24.5164 15.968 24.9084 15.752 25.2444 15.416V9.104C25.2444 7.568 24.7564 6.8 23.7804 6.8C23.3804 6.8 22.9884 6.92 22.6044 7.16C22.2204 7.4 21.9164 7.72 21.6924 8.12V15.416C22.0604 15.704 22.5404 15.92 23.1324 16.064L23.0364 17H16.9644L17.0364 16.016ZM43.7154 17.168C41.8594 17.168 40.4594 16.648 39.5154 15.608C38.5874 14.552 38.1234 13.112 38.1234 11.288C38.1234 9.384 38.6194 7.896 39.6114 6.824C40.6034 5.736 42.0354 5.192 43.9074 5.192C45.6994 5.192 46.9714 5.608 47.7234 6.44C48.4754 7.272 48.8994 8.512 48.9954 10.16L48.3234 11.168H41.5554V11.552C41.5554 12.896 41.7474 13.896 42.1314 14.552C42.5154 15.192 43.2354 15.512 44.2914 15.512C44.8994 15.512 45.4914 15.408 46.0674 15.2C46.6594 14.976 47.1714 14.64 47.6034 14.192L48.5634 15.128C47.9074 15.784 47.2194 16.288 46.4994 16.64C45.7954 16.992 44.8674 17.168 43.7154 17.168ZM45.5874 10.016C45.5874 8.736 45.4594 7.808 45.2034 7.232C44.9474 6.656 44.4834 6.368 43.8114 6.368C42.3554 6.368 41.6034 7.648 41.5554 10.208L45.5874 10.016ZM50.6441 21.08C50.9481 21.016 51.1801 20.944 51.3401 20.864C51.5161 20.784 51.7001 20.656 51.8921 20.48V8.504C51.8921 7.928 51.7801 7.536 51.5561 7.328C51.3481 7.104 50.9641 6.976 50.4041 6.944L50.4761 6.008C50.5881 5.976 50.7001 5.952 50.8121 5.936C50.9401 5.92 51.0521 5.904 51.1481 5.888C51.4201 5.84 51.9481 5.744 52.7321 5.6C53.5161 5.44 54.0921 5.304 54.4601 5.192L54.8921 5.408L55.1321 7.016C55.8201 5.8 56.9561 5.192 58.5401 5.192C59.9641 5.192 61.0601 5.672 61.8281 6.632C62.5961 7.592 62.9801 9 62.9801 10.856C62.9801 12.92 62.5161 14.488 61.5881 15.56C60.6761 16.632 59.3721 17.168 57.6761 17.168C57.1801 17.168 56.7481 17.12 56.3801 17.024C56.0281 16.928 55.6601 16.784 55.2761 16.592V20.48C55.7561 20.784 56.3481 21 57.0521 21.128L56.9561 22.064H50.5481L50.6441 21.08ZM56.8601 15.896C58.5881 15.896 59.4521 14.216 59.4521 10.856C59.4521 9.416 59.2921 8.352 58.9721 7.664C58.6521 6.976 58.1561 6.632 57.4841 6.632C56.7961 6.632 56.2521 6.968 55.8521 7.64C55.4681 8.312 55.2761 9.288 55.2761 10.568V15.416C55.4521 15.544 55.6841 15.656 55.9721 15.752C56.2601 15.848 56.5561 15.896 56.8601 15.896ZM64.4019 16.016C64.7059 15.952 64.9379 15.88 65.0979 15.8C65.2739 15.72 65.4579 15.592 65.6499 15.416V3.608C65.6499 3.016 65.5379 2.616 65.3139 2.408C65.1059 2.184 64.7219 2.056 64.1619 2.024L64.2339 1.088C64.4739 1.04 64.9059 0.967999 65.5299 0.871999C67.2419 0.583999 68.2739 0.383999 68.6259 0.271999L69.0579 0.511999V15.416C69.2019 15.544 69.4099 15.672 69.6819 15.8C69.9539 15.912 70.2499 16 70.5699 16.064L70.4979 17H64.3059L64.4019 16.016ZM74.9991 17.168C73.8951 17.168 73.0471 16.912 72.4551 16.4C71.8631 15.888 71.5671 15.144 71.5671 14.168C71.5671 13.128 71.9671 12.312 72.7671 11.72C73.5671 11.112 74.7431 10.752 76.2951 10.64L78.4071 10.496V9.104C78.4071 7.984 78.2711 7.208 77.9991 6.776C77.7431 6.344 77.2871 6.128 76.6311 6.128C76.1831 6.128 75.8071 6.248 75.5031 6.488C75.2151 6.712 75.0711 7 75.0711 7.352C75.0711 7.752 75.2471 8.088 75.5991 8.36C75.5191 8.76 75.3271 9.08 75.0231 9.32C74.7191 9.56 74.3511 9.68 73.9191 9.68C73.3911 9.68 72.9511 9.512 72.5991 9.176C72.2471 8.84 72.0711 8.424 72.0711 7.928C72.0711 7.08 72.4711 6.416 73.2711 5.936C74.0871 5.44 75.2071 5.192 76.6311 5.192C78.3271 5.192 79.6071 5.512 80.4711 6.152C81.3511 6.792 81.7911 7.736 81.7911 8.984V15.512C81.9031 15.64 82.0951 15.768 82.3671 15.896C82.6391 16.008 82.9271 16.096 83.2311 16.16L83.1351 17H78.8151L78.5751 15.632C78.0471 16.16 77.4951 16.552 76.9191 16.808C76.3431 17.048 75.7031 17.168 74.9991 17.168ZM76.1751 15.608C76.9111 15.608 77.6551 15.168 78.4071 14.288V11.672L77.2071 11.768C76.3271 11.832 75.6951 12.032 75.3111 12.368C74.9271 12.704 74.7351 13.216 74.7351 13.904C74.7351 14.448 74.8551 14.872 75.0951 15.176C75.3511 15.464 75.7111 15.608 76.1751 15.608ZM84.5364 16.016C84.8404 15.952 85.0724 15.88 85.2324 15.8C85.4084 15.72 85.6004 15.592 85.8084 15.416V8.504C85.8084 7.928 85.6964 7.536 85.4724 7.328C85.2484 7.104 84.8564 6.976 84.2964 6.944L84.3684 6.008L85.1604 5.864C86.8564 5.576 87.9604 5.352 88.4724 5.192L88.9044 5.408L89.0964 6.8C89.5284 6.288 90.0404 5.896 90.6324 5.624C91.2404 5.336 91.9044 5.192 92.6244 5.192C93.7124 5.192 94.5764 5.52 95.2164 6.176C95.8724 6.832 96.2004 7.736 96.2004 8.888V15.416C96.6004 15.72 97.1124 15.936 97.7364 16.064L97.6404 17H91.5684L91.6404 16.064C92.1204 15.968 92.5124 15.752 92.8164 15.416V9.104C92.8164 8.32 92.6884 7.744 92.4324 7.376C92.1924 6.992 91.8244 6.8 91.3284 6.8C90.9124 6.8 90.5044 6.92 90.1044 7.16C89.7204 7.4 89.4164 7.72 89.1924 8.12V15.416C89.5604 15.704 90.0404 15.92 90.6324 16.064L90.5364 17H84.4644L84.5364 16.016ZM99.0208 16.016C99.3248 15.952 99.5568 15.88 99.7168 15.8C99.8928 15.72 100.085 15.592 100.293 15.416V8.504C100.293 7.928 100.181 7.536 99.9568 7.328C99.7328 7.104 99.3408 6.976 98.7808 6.944L98.8528 6.008L99.6448 5.864C101.341 5.576 102.445 5.352 102.957 5.192L103.389 5.408L103.581 6.8C104.013 6.288 104.525 5.896 105.117 5.624C105.725 5.336 106.389 5.192 107.109 5.192C108.197 5.192 109.061 5.52 109.701 6.176C110.357 6.832 110.685 7.736 110.685 8.888V15.416C111.085 15.72 111.597 15.936 112.221 16.064L112.125 17H106.053L106.125 16.064C106.605 15.968 106.997 15.752 107.301 15.416V9.104C107.301 8.32 107.173 7.744 106.917 7.376C106.677 6.992 106.309 6.8 105.813 6.8C105.397 6.8 104.989 6.92 104.589 7.16C104.205 7.4 103.901 7.72 103.677 8.12V15.416C104.045 15.704 104.525 15.92 105.117 16.064L105.021 17H98.9488L99.0208 16.016ZM118.809 17.168C116.953 17.168 115.553 16.648 114.609 15.608C113.681 14.552 113.217 13.112 113.217 11.288C113.217 9.384 113.713 7.896 114.705 6.824C115.697 5.736 117.129 5.192 119.001 5.192C120.793 5.192 122.065 5.608 122.817 6.44C123.569 7.272 123.993 8.512 124.089 10.16L123.417 11.168H116.649V11.552C116.649 12.896 116.841 13.896 117.225 14.552C117.609 15.192 118.329 15.512 119.385 15.512C119.993 15.512 120.585 15.408 121.161 15.2C121.753 14.976 122.265 14.64 122.697 14.192L123.657 15.128C123.001 15.784 122.313 16.288 121.593 16.64C120.889 16.992 119.961 17.168 118.809 17.168ZM120.681 10.016C120.681 8.736 120.553 7.808 120.297 7.232C120.041 6.656 119.577 6.368 118.905 6.368C117.449 6.368 116.697 7.648 116.649 10.208L120.681 10.016ZM129.938 17.168C128.514 17.168 127.418 16.688 126.65 15.728C125.898 14.768 125.522 13.36 125.522 11.504C125.522 9.456 125.978 7.896 126.89 6.824C127.818 5.736 129.13 5.192 130.826 5.192C131.306 5.192 131.722 5.24 132.074 5.336C132.442 5.432 132.818 5.576 133.202 5.768V3.608C133.202 3.016 133.082 2.616 132.842 2.408C132.602 2.184 132.162 2.056 131.522 2.024L131.594 1.088C131.882 1.024 132.33 0.951999 132.938 0.871999C133.098 0.839999 133.578 0.759999 134.378 0.631999C135.194 0.503999 135.794 0.383999 136.178 0.271999L136.586 0.511999V15.512C136.73 15.64 136.93 15.768 137.186 15.896C137.458 16.008 137.738 16.096 138.026 16.16L137.93 17H133.61L133.298 15.44C132.61 16.592 131.49 17.168 129.938 17.168ZM131.018 15.728C131.722 15.728 132.258 15.368 132.626 14.648C133.01 13.912 133.202 12.824 133.202 11.384V6.944C133.026 6.816 132.794 6.712 132.506 6.632C132.234 6.536 131.946 6.488 131.642 6.488C129.898 6.488 129.026 8.16 129.026 11.504C129.026 12.944 129.186 14.008 129.506 14.696C129.826 15.384 130.33 15.728 131.018 15.728Z"
            />
          </svg>
        </div>

        <p className="text-base mb-1">
          {isResetPassword
            ? t("auth.resetPassword")
            : isRegistering
            ? t("auth.createAccountSubtitle")
            : t("auth.signInSubtitle")}
        </p>

        <AnimatePresence mode="wait">
          {isResetPassword ? (
            <motion.div
              key="reset-password"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <Input
                label={t("auth.email")}
                type="email"
                name="resetEmail"
                id="resetEmail"
                fullWidth
                margin="normal"
                value={resetEmail}
                onChange={(e) => {
                  setResetEmail(e.target.value);
                  if (resetEmailError) setResetEmailError(null);
                }}
                disabled={loading}
                error={!!resetEmailError}
                helperText={resetEmailError}
                labelPosition="top-left"
              />
              <div className="flex gap-2 w-full">
                <button
                  className="btn btn-primary w-full h-[42px] flex items-center justify-center text-[16px] no-underline"
                  onClick={handleResetPassword}
                  disabled={loading || !validateEmail(resetEmail)}
                >
                  {t("auth.resetPassword")}
                </button>
                <button
                  className="btn btn-primary w-full h-[42px] flex items-center justify-center text-[16px] no-underline font-normal"
                  onClick={() => {
                    setIsResetPassword(false);
                    clearResetFields();
                  }}
                  disabled={loading}
                >
                  {t("common.cancel")}
                </button>
              </div>
              {error && (
                <p className="mt-4 text-sm p-3 rounded-full text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  {error}
                </p>
              )}
            </motion.div>
          ) : isRegistering ? (
            <motion.div
              key="register"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <Input
                label={t("auth.firstName")}
                type="text"
                name="firstName"
                id="firstName"
                fullWidth
                margin="dense"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                error={!!error && stringExtension.isEmpty(firstName)}
                helperText={
                  !!error && stringExtension.isEmpty(firstName)
                    ? t("auth.errors.firstNameRequired")
                    : ""
                }
                labelPosition="top-left"
              />
              <Input
                label={t("auth.lastName")}
                type="text"
                name="lastName"
                id="lastName"
                fullWidth
                margin="dense"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                error={!!error && stringExtension.isEmpty(lastName)}
                helperText={
                  !!error && stringExtension.isEmpty(lastName)
                    ? t("auth.errors.lastNameRequired")
                    : ""
                }
                labelPosition="top-left"
              />
              <Input
                label={t("auth.email")}
                type="email"
                name="email"
                id="email"
                fullWidth
                margin="dense"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                disabled={loading}
                error={
                  (!!error && stringExtension.isEmpty(email)) || !!emailError
                }
                helperText={
                  emailError ||
                  (!!error && stringExtension.isEmpty(email)
                    ? t("auth.errors.emailRequired")
                    : "")
                }
                labelPosition="top-left"
              />
              <Input
                label={t("auth.password")}
                type="password"
                name="password"
                id="password"
                fullWidth
                margin="dense"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setRepeatPasswordError(null);
                  if (passwordError) setPasswordError(null);
                }}
                disabled={loading}
                error={
                  (!!error && stringExtension.isEmpty(password)) ||
                  !!passwordError
                }
                helperText={
                  passwordError ||
                  (!!error && stringExtension.isEmpty(password)
                    ? t("auth.errors.passwordRequired")
                    : "")
                }
                showPasswordToggle={true}
                labelPosition="top-left"
              />
              <Input
                label={t("auth.repeatPassword")}
                type="password"
                name="repeatPassword"
                id="repeatPassword"
                fullWidth
                margin="dense"
                value={repeatPassword}
                onChange={(e) => {
                  setRepeatPassword(e.target.value);
                  if (e.target.value !== password) {
                    setRepeatPasswordError(t("auth.errors.passwordsNotMatch"));
                  } else {
                    setRepeatPasswordError(null);
                  }
                }}
                disabled={loading}
                error={
                  (!!error && stringExtension.isEmpty(repeatPassword)) ||
                  !!repeatPasswordError
                }
                helperText={
                  repeatPasswordError ||
                  (!!error && stringExtension.isEmpty(repeatPassword)
                    ? t("auth.errors.repeatPasswordRequired")
                    : "")
                }
                showPasswordToggle={true}
                labelPosition="top-left"
              />
              <div className="w-full mt-1 mb-1 text-left">
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => {
                      setAgreedToTerms(e.target.checked);
                      if (e.target.checked) setTermsError(null);
                    }}
                    disabled={loading}
                    className="mt-1 h-4 w-4 shrink-0 rounded cursor-pointer"
                    style={{ accentColor: 'var(--color-primary-40)' }}
                  />
                  <span
                    className="text-sm leading-snug"
                    style={{ color: isDark ? 'var(--color-grey-80)' : 'var(--color-grey-30)' }}
                  >
                    <Trans
                      i18nKey="auth.agreeToTerms"
                      components={{
                        termsLink: (
                          <a
                            href={ROUTES.TERMS_OF_SERVICE}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline font-medium"
                            style={{ color: isDark ? 'var(--color-primary-90)' : 'var(--color-primary-40)' }}
                          />
                        ),
                        privacyLink: (
                          <a
                            href={ROUTES.PRIVACY_POLICY}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline font-medium"
                            style={{ color: isDark ? 'var(--color-primary-90)' : 'var(--color-primary-40)' }}
                          />
                        ),
                      }}
                    />
                  </span>
                </label>
                {termsError && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {termsError}
                  </p>
                )}
              </div>
              <div className="flex gap-2 w-full">
                <button
                  className="btn btn-primary w-full h-[42px] flex items-center justify-center text-[16px] no-underline font-normal"
                  onClick={handleRegister}
                  disabled={loading || !isRegisterFormValid()}
                >
                  {t("auth.register")}
                </button>
                <button
                  className="btn btn-primary w-full h-[42px] flex items-center justify-center text-[16px] no-underline font-normal"
                  onClick={() => {
                    setIsRegistering(false);
                    clearInputFields();
                  }}
                  disabled={loading}
                >
                  {t("common.cancel")}
                </button>
              </div>
              {error && isRegistering && (
                <p className="mt-2 text-sm p-3 rounded-full text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  {error}
                </p>
              )}
            </motion.div>
          ) : (
            <>
              <motion.div
                key="login-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                style={{ width: "100%" }}
              >
                <Input
                  label={t("auth.email")}
                  type="email"
                  name="loginEmail"
                  id="loginEmail"
                  fullWidth
                  margin="normal"
                  value={email || ""}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  error={!!emailError}
                  helperText={emailError}
                  labelPosition="top-left"
                />
                <Input
                  label={t("auth.password")}
                  type="password"
                  name="loginPassword"
                  id="loginPassword"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setRepeatPasswordError(null);
                    if (passwordError) setPasswordError(null);
                  }}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  error={!!passwordError}
                  helperText={passwordError}
                  showPasswordToggle={true}
                  labelPosition="top-left"
                />
                {error && (
                  <p className="mb-2 text-sm p-1 rounded text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">
                    {error}
                  </p>
                )}
                <div className="w-full">
                  <button
                    className="btn btn-primary w-full h-[42px] flex items-center justify-center text-[16px] no-underline"
                    onClick={handleEmailLogin}
                    disabled={loading}
                  >
                    {t("auth.signIn")}
                  </button>
                </div>
                <div className="mt-2 text-center">
                  <p
                    className="text-sm cursor-pointer mt-4 hover:underline text-primary-40 dark:!text-primary-90"
                    onClick={() => {
                      setIsResetPassword(true);
                      clearInputFields();
                    }}
                  >
                    {t("auth.forgotPassword")}
                  </p>
                </div>
              </motion.div>

              <motion.div
                key="external-providers"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                style={{ width: "100%" }}
              >
                <div className="w-full flex items-center mt-4 mb-4">
                  <hr className="flex-grow" />
                  <p className="mx-2 text-sm">
                    {t("auth.or").toUpperCase()}{" "}
                    {t("auth.continueWith").toUpperCase()}
                  </p>
                  <hr className="flex-grow" />
                </div>
                <div
                  className={`
                    w-full flex items-center flex-col gap-5
                    ${loading ? "pointer-events-none" : "pointer-events-auto"}
                  `}
                >
                  <LoginButton
                    text={t("auth.signInWithGoogle")}
                    icon="/assets/images/icons/google-icon.svg"
                    onClick={() => handleLoginAsync("Google")}
                    disabled={loading}
                  />

                  <LoginButton
                    text={t("auth.signInWithMicrosoft")}
                    icon="/assets/images/icons/microsoft-icon.svg"
                    onClick={() => handleLoginAsync("Microsoft")}
                    disabled={loading}
                  />

                  <LoginButton
                    text={t("auth.signInWithFacebook")}
                    icon="/assets/images/icons/facebook-icon.svg"
                    onClick={() => handleLoginAsync("Facebook")}
                    disabled={loading}
                  />

                  <div className="mt-2 text-center">
                    <p className="text-sm">
                      {t("auth.dontHaveAccount")}{" "}
                      <span
                        className="cursor-pointer hover:underline text-primary-40 dark:!text-primary-90 font-medium"
                        onClick={() => {
                          setIsRegistering(true);
                          clearInputFields();
                        }}
                      >
                        {t("auth.signUp")}
                      </span>
                    </p>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LoginPage;
